/**
 * @file embed-map.tsx
 * @module EmbedMap
 * @description Iegultā (iframe) kartes komponente, izmantojot Mapbox GL JS, NPP
 *              publiskajam portālam. Vieglāka alternatīva pilnajai OpenLayers kartei,
 *              paredzēta iegulšanai ārējās tīmekļa vietnēs. Atbalsta iepriekš definētas
 *              slāņu kopas un vienkāršotu klikšķa/virzīšanās interakciju.
 */

import { PGTILES_1, PGTILES_2, LAU_TILES } from "./definitions/uvis-definition";
import { BASE_MAP } from "./definitions/base-map-definition";
import { createContext, useEffect, useState } from "react";
import { AttributionControl, LayerSpecification, Map, SpriteSpecification } from "maplibre-gl";

import EmbedMapHover from "../embed-map-click/embed-map-hover";
import EmbedMapClick from "../embed-map-click/embed-map-click";

import "maplibre-gl/dist/maplibre-gl.css";
import { EmbedWebsocket } from "./embed-websocket";
import { summerPreset, winterPreset } from "./embed-map-presets";

/** Context interface for sharing the MapLibre map instance and active layers */
interface EmbedMapContextInterface {
	/** MapLibre GL map instance (undefined before initialization) */
	map?: Map;
	/** Currently active layer specifications based on the selected preset */
	layers: LayerSpecification[];
}

/** React context providing the embed map instance to child components (click, hover, websocket) */
export const EmbedMapContext = createContext<EmbedMapContextInterface>({ layers: [] });

const getLayers = () => {
	switch (getMode()) {
		case "summer":
			return summerPreset;
		case "winter":
			return winterPreset;
		default:
			return winterPreset;
	}
};

const getMode = () => {
	const url = window.location.href;
	const params = new URL(url).searchParams;
	return params.get("mode");
};

export default function EmbedMapProvider() {
	const [map, setMap] = useState<Map>();
	const [layers, setLayers] = useState<LayerSpecification[]>([]);

	useEffect(() => {
		const sprite: SpriteSpecification = [
			{ id: "osm", url: BASE_MAP.sprite as string },
			{ id: "default", url: window.location.origin + "/embed-sprites/sprite" },
		];

		const layers = getLayers();

		const style = {
			version: 8,
			sources: {
				pgtiles: PGTILES_1,
				pgtiles_2: PGTILES_2,
				lau: LAU_TILES,
				osm: {
					type: "raster",
					tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
					tileSize: 256,
				},
			},
			layers,
			sprite,
		};
		const mapInstance = new Map({
			style: style as any,
			container: "map",
			center: [24.1056, 56.9677],
			zoom: 8,
			maxZoom: 19,
			antialias: false,
			hash: true,
			fadeDuration: 0,
			attributionControl: false,
		}).addControl(
			new AttributionControl({
				compact: true,
				customAttribution: [
					"<a href='https://maplibre.org/'>MapLibre</a>",
					"© <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap contributors</a>",
					"© Latvijas Valsts Ceļi",
				],
			}),
			"bottom-right"
		);

		setMap(mapInstance);
		setLayers(layers);

		return () => {
			mapInstance.remove();
		};
	}, []);

	return (
		<EmbedMapContext.Provider value={{ map, layers }}>
			<div id="map" className="!absolute !inset-0 z-10"></div>
			<EmbedMapClick />
			<EmbedMapHover />
			<EmbedWebsocket />
		</EmbedMapContext.Provider>
	);
}
